import { Typography } from '@material-ui/core';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { Translation, useNavigate } from '@ps/web-shared-components';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import RegulatoryCheckboxes from '@swf/components/RegulatoryCheckboxes/RegulatoryCheckboxes';
import { StatusCode } from '@swf/core/enums/statusCode';
import useFeature from '@swf/core/hooks/useFeature';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import createSingleton from '../../../../hooks/useSingleton';
import { PageType } from '../../../../types/ContentOverlay';
import Container from '../../../Container/Container';
import ContentOverlay from '../../../ContentOverlay/ContentOverlay';
import DOB from '../../../DOB/DOB';
import Prompt from '../../../Prompt/Prompt';
import SignupBlackListedUser from '../SignupBlackListedUser/SignupBlackListedUser';
import SignupError from '../SignupError/SignupError';

import {
  TXTCLI_Terms_X_Conditions,
  TXTCLI_Your_agreement,
  TXTWCL_Continue_to_personal_details,
  TXTWCL_Next_upXX,
  TXTWCL_The_legal_stuffXX,
  TXTWCL_We_need_a_few_more_details_to__ELL,
  TXTWCL_We_need_your_date_of_birth_to__ELL,
  TXTWCL_YouXre_in,
  TXTWCL_Your_username_and_password_hav_ELL
} from './SignupLegal.messages.json';
import CreateAccountButton from './components/CreateAccountButton/CreateAccountButton';
import InfoBox from './components/InfoBox/InfoBox';

const [getValue] = createSingleton(false);

const SignupLegal: React.FunctionComponent = () => {
  const [pageType, setPageType] = useState<PageType>();
  const legalAge = useSelector(select.cms.getLegalAge);
  const blacklistCheck = useFeature(FeatureFlag.BlackListCheck);
  const isSignupOneEnabled = useFeature(FeatureFlag.SwcSignupOnePage);
  const hasBeenShown = getValue();
  const isSwcSignupRmfEcoEransitionTwo = useFeature(FeatureFlag.SwcSignupRmfEcoEransitionTwo);
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const isCAON = useSelector(select.cms.getSiteId) === Sites.CA_ON;
  const variant = 'first';
  const {
    data: { signupPassed }
  } = useWizardContext();
  const onEulaLinkClick = () => setPageType(PageType.Eula);
  const headerProps =
    blacklistCheck || isSiteUS ? TXTCLI_Your_agreement : TXTCLI_Terms_X_Conditions;
  const navigate = useNavigate();
  const { error } = useWizardContext();

  if (error?.globalErrors?.length) {
    const { code } = error.globalErrors[0] || {};
    switch (true) {
      case code === StatusCode.PLAYER_BLACKLISTED.toString():
        return <SignupBlackListedUser />;
      case code === StatusCode.ERR_ACCOUNT_CREATION_FAILED.toString():
        return <SignupError />;
    }
  }

  return (
    <Container
      page={SignupLegal}
      headerProps={{
        title: <Translation {...headerProps} />,
        buttons: { back: true }
      }}
      pageTopTitle={<Translation {...TXTWCL_The_legal_stuffXX} />}
      pageTopSubtitle={
        blacklistCheck ? (
          ''
        ) : (
          <Translation {...TXTWCL_We_need_your_date_of_birth_to__ELL} values={{ 0: legalAge }} />
        )
      }
    >
      {!isSignupOneEnabled &&
        signupPassed &&
        !hasBeenShown &&
        !isSwcSignupRmfEcoEransitionTwo &&
        blacklistCheck && (
          <Prompt
            title={{ translation: TXTWCL_YouXre_in }}
            secondaryTitle={{ translation: TXTWCL_Next_upXX }}
            primaryButton={{ translation: TXTWCL_Continue_to_personal_details }}
            noCloseButton
            onPrimaryButtonClick={() => navigate('/account/cashier/rmf')}
            variant="celebration"
          >
            <Typography>
              <Translation {...TXTWCL_We_need_a_few_more_details_to__ELL} />
              <Translation {...TXTWCL_Your_username_and_password_hav_ELL} />
            </Typography>
          </Prompt>
        )}
      <InfoBox onEulaLinkClick={onEulaLinkClick} />
      <fieldset>
        {!blacklistCheck && <DOB />}
        <RegulatoryCheckboxes
          variant={variant}
          isSelectAllToggleVisible={variant === 'first' && isCAON}
          onEulaLinkClick={onEulaLinkClick}
          onPrivacyLinkClick={() => setPageType(PageType.Privacy)}
        />
      </fieldset>
      <CreateAccountButton />
      {pageType && (
        <ContentOverlay
          {...(pageType === PageType.Eula && { sectionId: 'part-B-section6' })}
          pageType={pageType}
          onClose={() => setPageType(undefined)}
        />
      )}
    </Container>
  );
};

SignupLegal.displayName = 'SignupLegal';

export default SignupLegal;
